import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import {
  AutocompleteInput,
  Form,
  ReferenceInput,
  required,
  TextInput,
  useCreate,
  useNotify,
  useRefresh,
  useUpdate
} from "react-admin";
import Modal from "components/Modal";
import LoadingButton from "@mui/lab/LoadingButton";
import { validateSpace } from "utils/index";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";

function EditQuestionTagFormBody({ tag }: { tag?: any }) {
  const { control, setValue } = useFormContext();
  const categoryId = useWatch({ control, name: 'questionCategory' });
  const prevCategoryId = React.useRef(categoryId);
  React.useEffect(() => {
    if (prevCategoryId.current !== undefined && prevCategoryId.current !== categoryId) {
      setValue('questionSubcategory', null);
    }
    prevCategoryId.current = categoryId;
  }, [categoryId, setValue]);

  return (
    <Stack spacing={1}>
      <Typography variant="h6" mb={2}>
        {tag ? "Редактировать тег" : "Создать тег"}
      </Typography>
      <ReferenceInput
        reference={'question-category'}
        source="questionCategory"
        optionText="title"
        alwaysOn
      >
        <AutocompleteInput
          filterToQuery={(searchText) => ({ category: searchText })}
          label="Категория" size={'small'} validate={required()}/>
      </ReferenceInput>
      <ReferenceInput
        reference={'question-subcategory'}
        source="questionSubcategory"
        optionText="name"
        alwaysOn
        filter={categoryId ? { category: categoryId } : {}}
      >
        <AutocompleteInput
          filterToQuery={(searchText) => ({ subcategory: searchText })}
          label="Подкатегория"
          size={'small'}
          disabled={!categoryId}
        />
      </ReferenceInput>
      <TextInput
        fullWidth
        source={'title'}
        label="Название тега"
        size={"small"}
        validate={[required(), validateSpace]}
      />
    </Stack>
  )
}

interface EditQuestionTagModalProps {
  open: boolean;
  onClose: () => void;
  tag?: { id: string; title: string };
  category?: { id: string; title: string };
  subcategory?: any;
}

const EditQuestionTagModal: React.FC<EditQuestionTagModalProps> = ({
                                                                     open,
                                                                     onClose,
                                                                     tag,
                                                                     category,
                                                                     subcategory,
                                                                   }) => {

  const notify = useNotify();
  const refresh = useRefresh();

  const methods = useForm()

  const [create, { isPending: isCreating }] = useCreate();
  const [update, { isPending: isUpdating }] = useUpdate();

  const handleSaveEdit = async (data: any) => {
    if (tag) {
      await update(
        `tag/${tag.id}`,
        {
          id: tag.id,
          data: {
            title: data.title,
            questionCategoryId: data.questionCategory,
            questionSubcategoryId: data.questionSubcategory,
          }
        },
        {
          onSuccess: () => {
            notify("Тег обновлен", { type: "success" });
            refresh();
            onClose();
          },
          onError: () => {
            notify("Ошибка при обновлении тега", { type: "error" });
          },
        }
      )
    } else {
      await create(
        'tag',
        {
          data: {
            title: data.title,
            questionCategoryId: data.questionCategory,
            questionSubcategoryId: data.questionSubcategory,
          }
        },
        {
          onSuccess: () => {
            notify("Тег создан", { type: "success" });
            refresh();
            onClose();
          },
          onError: () => {
            notify("Ошибка при создании тега", { type: "error" });
          },
        }
      )
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <FormProvider {...methods}>
        <Form onSubmit={handleSaveEdit} defaultValues={{
          questionCategory: category?.id,
          questionSubcategory: subcategory?.id,
          title: tag?.title,
        }}>
          <EditQuestionTagFormBody tag={tag}/>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={onClose}>Отмена</Button>
            <LoadingButton
              loading={isUpdating || isCreating}
              type={'submit'}
              variant="contained"
              color="primary"
              sx={{ ml: 2 }}>
              Сохранить
            </LoadingButton>
          </Box>
        </Form>
      </FormProvider>
    </Modal>
  );
};

export default EditQuestionTagModal;
